package com.dotdrives.model

import kotlin.time.Instant

/** One `drive.phase_changed` event, reduced to what the median computation needs. */
data class PhaseChangeSample(
    val driveId: String,
    val toPhaseId: String,
    val occurredAt: Instant,
)

/**
 * Consecutive `drive.phase_changed` events per drive give one historical duration sample per
 * phase entry; `percentile_cont(0.5)` over those samples, grouped by phase, is the per-phase
 * median (docs/schema.md, "Stale-drive-nudge specifically"). Pure and client-independent so
 * the arithmetic can be covered directly, not only through a fake in a controller test.
 */
fun phaseMedians(events: List<PhaseChangeSample>): Map<String, Double> {
    val samples = mutableMapOf<String, MutableList<Double>>()
    events.groupBy(PhaseChangeSample::driveId).values.forEach { driveEvents ->
        val sorted = driveEvents.sortedBy(PhaseChangeSample::occurredAt)
        for (i in 0 until sorted.size - 1) {
            val entering = sorted[i]
            val leaving = sorted[i + 1]
            val durationDays = (leaving.occurredAt - entering.occurredAt).inWholeHours / 24.0
            samples.getOrPut(entering.toPhaseId) { mutableListOf() } += durationDays
        }
    }
    return samples.mapValues { (_, values) -> percentileContOf(values, 0.5) }
}

/** Postgres `percentile_cont(p)`: `rank = p * (n - 1)`, linearly interpolated between the two
 * bracketing order statistics -- not a naive midpoint, and not `nearest-rank`. */
fun percentileContOf(values: List<Double>, percentile: Double): Double {
    if (values.isEmpty()) return 0.0
    val sorted = values.sorted()
    if (sorted.size == 1) return sorted[0]
    val rank = percentile * (sorted.size - 1)
    val lowIndex = kotlin.math.floor(rank).toInt()
    val highIndex = kotlin.math.ceil(rank).toInt()
    if (lowIndex == highIndex) return sorted[lowIndex]
    val fraction = rank - lowIndex
    return sorted[lowIndex] + (sorted[highIndex] - sorted[lowIndex]) * fraction
}
